import { useState, useMemo, useCallback, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Typography, makeStyles } from '@material-ui/core';
import AlertDialog from '../../shared/alertDialog';
import PageContainer from '../../shared/pageContainer';
import UserCompare from '../../shared/userCompare';
import NotificationCard from '../../shared/notificationCard';
import SelectField from '../../shared/selectField';
import PresetDropdown from '../../shared/presetDropdown';
import {
  API_USERS_BASIC,
  API_REPORT_USER_COMPARE,
} from '../../../constants/apiRoutes';
import { APP_PAGES } from '../../../constants/pages';
import { PRESETS } from '../../../constants/presets';
import {
  SURVEY_TYPES,
  SURVEY_TYPE_FILTER_OPTIONS,
} from '../../../constants/survey';
import { SYSTEM_ROLES, SYSTEM_ROLE_OPTIONS } from '../../../constants/roles';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { USER_EMPLOYMENT_INFORMATION } from '../../../constants/people';
import { sticky } from '../../../constants/helperCssRules';
import { peopleSelector } from '../../../store/selectors/peopleSelector';
import { jobTitlesSelector } from '../../../store/selectors/jobTitlesSelector';
import { levelsAndTracksSelector } from '../../../store/selectors/levelsAndTracksSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { getAllUsers, clearAllUsers } from '../../../store/modules/people';
import { getJobTitles, clearJobTitles } from '../../../store/modules/jobTitles';
import {
  addNewPreset,
  deleteSelectedPreset,
  editPresetActivity,
} from '../../../store/modules/organizationSettings';
import {
  getTagsCategories,
  clearTagsCategories,
} from '../../../store/modules/tags';
import {
  getLevelsAndTracks,
  clearLevelsAndTracks,
} from '../../../store/modules/levelsAndTracks';
import {
  clearDivisionsAndDepartments,
  getDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import http from '../../../utility/http';
import {
  parseDuplicateParameters,
  showSuccessMessage,
} from '../../../utility/uiUtils';
import {
  areArraysContentsEqual,
  getItemById,
  isArrayEmpty,
  isValueInList,
} from '../../../utility/helpers';
import { prepareSurveyTypeFilterOptions } from '../../../utility/reportUtils';
import { preparePeopleFilters } from '../../../utility/people';
import { prepareRoles } from '../../../utility/roles';
import {
  NO_ACTIVE_PRESET,
  getActivePresetId,
  getActivePresetName,
  getFilteredPresets,
  getPlaceholderName,
  handleDisabledPresetMessage,
  handleHasMaxPresets,
  handlePresetName,
  getSelectedSurveyType,
} from '../../../utility/presets';
import { useTranslations } from '../../../utility/useTranslations';
import {
  isUserProfileAccessibleCheck,
  hasScopedAuthPermissionCheck,
} from '../../../utility/permissions';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {},
  pageDescription: {
    padding: spacing(8, 0, 6, 0),
    width: '100%',
    ...sticky(primary.white, 105),
  },
  userSelectWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 300px))',
    gridColumnGap: 16,
    paddingBottom: spacing(6),
    ...sticky(primary.white, 179),
  },
  userSelect: {
    minWidth: 300,
    maxWidth: 300,
  },
}));

const COMPARE_PRESET_ID = PRESETS.COMPARE;
const DROPDOWN_WIDTH = '340px';

const CompareReportPage = ({
  navigate,
  auth,
  accessControl,
  presets,
  ...rest
}) => {
  const classes = useStyles();
  const compareRef = useRef();
  const translations = useTranslations(APP_PAGES.COMPARE);

  const dispatch = useDispatch();
  const location = useLocation();
  const [tempState, setTempState] = useState(location?.state);
  const { allUsers } = useSelector(peopleSelector);
  const { jobTitles } = useSelector(jobTitlesSelector);
  const { tracks } = useSelector(levelsAndTracksSelector);
  const { divisions, departments } = useSelector(
    divisionsAndDepartmentsSelector
  );
  const { categories } = useSelector(tagsSelector);

  const [isLoading, setIsLoading] = useState(true);
  const [initialPreset, setInitialPreset] = useState(null);
  const initialPeriod = tempState?.period || null;
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [createdForUser, setCreatedForUser] = useState(tempState?.user || null);

  const [alreadySavedMessage, setAlreadySavedMessage] = useState('');
  const [createdForDisabledPresetMessage, setCreatedForDisabledPresetMessage] =
    useState('');
  const [createdForDisabledMessage, setCreatedForDisabledMessage] =
    useState('');
  const [
    compareWithDisabledPresetMessage,
    setCompareWithDisabledPresetMessage,
  ] = useState('');
  const [filterDataForPreset, setFilterDataForPreset] = useState({
    createdFor: {},
    compareWith: {},
    createdForReviewers: [],
    compareWithReviewers: [],
  });

  const [isDeletePresetDialogOpened, setIsDeletePresetDialogOpened] =
    useState(false);
  const [defaultPlaceholder, setDefaultPlaceholder] = useState(false);
  const [presetPlaceholderName, setPresetPlaceholderName] = useState('');
  const presetState = {
    defaultPlaceholder,
    presetPlaceholderName,
    isLoading,
  };
  const activePresetId = tempState
    ? NO_ACTIVE_PRESET
    : getActivePresetId(presets, COMPARE_PRESET_ID);
  const filteredPresets = getFilteredPresets(presets, COMPARE_PRESET_ID);
  const activePresetName = getActivePresetName(filteredPresets, activePresetId);
  const handledPresetName = handlePresetName(
    filteredPresets,
    activePresetId,
    activePresetName,
    presetState,
    translations.presets.startSearch
  );
  const placeholderName = getPlaceholderName(
    handledPresetName,
    activePresetName,
    translations.presets.startSearch
  );

  const allAccessibleUsers = useMemo(
    () =>
      allUsers.filter(
        user =>
          user[USER_EMPLOYMENT_INFORMATION.ROLE] !== SYSTEM_ROLES.ASSOCIATE &&
          hasScopedAuthPermissionCheck(
            PERMISSION_CODES.REPORTS_COMPARE_VIEW,
            accessControl,
            auth.id,
            user.id
          )
      ),
    [allUsers, auth, accessControl]
  );
  const userFilters = useMemo(
    () =>
      !isLoading
        ? preparePeopleFilters(
            translations,
            jobTitles,
            tracks,
            divisions,
            departments,
            prepareRoles(translations.systemRoles, SYSTEM_ROLE_OPTIONS, true),
            [],
            categories,
            true
          )
        : [],
    [
      translations,
      isLoading,
      jobTitles,
      tracks,
      divisions,
      departments,
      categories,
    ]
  );
  const initialReviewers = tempState?.user ? allUsers : [];
  const initialSurveyType = tempState?.surveyType
    ? getSelectedSurveyType(SURVEY_TYPE_FILTER_OPTIONS, tempState.surveyType)
    : null;

  const goToUserProfilePage = userId => () => navigate(`/people/${userId}`);

  const canAccessUserProfileCheck = useCallback(
    userId => isUserProfileAccessibleCheck(accessControl, auth.id, userId),
    [auth, accessControl]
  );

  const onGetReviewers = useCallback(data => {
    const params = {
      ...data,
    };

    return http.get(API_USERS_BASIC, {
      params,
      paramsSerializer: d => parseDuplicateParameters(d),
    });
  }, []);

  const getInitialPreset = () => {
    const compareReportPagePresets = tempState
      ? null
      : presets[COMPARE_PRESET_ID];
    const existsPresetsArray =
      compareReportPagePresets && !isArrayEmpty(compareReportPagePresets);
    const tempInitialPreset = existsPresetsArray
      ? getItemById(compareReportPagePresets, presets.active[COMPARE_PRESET_ID])
      : undefined;
    if (tempInitialPreset) {
      const reviewedUser = tempInitialPreset?.data?.createdFor?.subject || null;
      const canViewReviewedUser = hasScopedAuthPermissionCheck(
        PERMISSION_CODES.REPORTS_COMPARE_VIEW,
        accessControl,
        auth.id,
        reviewedUser.id
      );
      setAlreadySavedMessage(translations.presets.used);
      setInitialPreset(tempInitialPreset);
      setSelectedPreset(tempInitialPreset);
      setCreatedForUser(
        reviewedUser && canViewReviewedUser ? reviewedUser : null
      );
    }
  };

  const getInitialData = useCallback(async () => {
    try {
      await Promise.all([
        getAllUsers(dispatch, {}, true),
        getJobTitles(dispatch),
        getTagsCategories(dispatch),
        getLevelsAndTracks(dispatch),
        getDivisionsAndDepartments(dispatch),
      ]);

      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [dispatch]);

  const cleanup = useCallback(() => {
    dispatch(clearAllUsers());
    dispatch(clearLevelsAndTracks());
    dispatch(clearDivisionsAndDepartments());
    dispatch(clearJobTitles());
    dispatch(clearTagsCategories());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
    getInitialPreset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getInitialData]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleResetPreset = useCallback(() => {
    editPresetActivity(dispatch, -1, COMPARE_PRESET_ID, false);
    setSelectedPreset(undefined);
    setInitialPreset(undefined);
    compareRef?.current?.updateCreatedForPreset(null);
    compareRef?.current?.updateCompareWithPreset(null);
    setAlreadySavedMessage('');
  }, [dispatch]);

  const areFilterAndPresetEqual = (selectedData, presetData) => {
    if (
      (!selectedData && !presetData) ||
      (!selectedData?.subject && !presetData)
    )
      return true;
    return (
      selectedData &&
      presetData &&
      areArraysContentsEqual(
        selectedData?.reviewer || [],
        presetData?.reviewer || []
      ) &&
      selectedData?.from_date === presetData.from_date &&
      selectedData?.to_date === presetData.to_date &&
      (selectedData?.subject.id === presetData?.subject.id ||
        selectedData?.subject === presetData?.subject.id) &&
      areArraysContentsEqual(selectedData?.survey_type, presetData.survey_type)
    );
  };

  const checkValueInPresets = useCallback(
    (createdFor, compareWith) => {
      const comparePresets = presets[COMPARE_PRESET_ID];
      setTempState(null);
      if (comparePresets && !isArrayEmpty(comparePresets)) {
        const activePreset = comparePresets.find(preset => {
          const presetData = preset?.data;

          return (
            areFilterAndPresetEqual(createdFor, presetData?.createdFor) &&
            areFilterAndPresetEqual(compareWith, presetData?.compareWith)
          );
        });
        if (activePreset) {
          editPresetActivity(
            dispatch,
            activePreset.id,
            COMPARE_PRESET_ID,
            true
          );
          setSelectedPreset(activePreset);
          setInitialPreset(undefined);
          setAlreadySavedMessage(translations.presets.used);
        } else {
          handleResetPreset();
        }
      }
    },
    [dispatch, handleResetPreset, presets, translations.presets.used]
  );

  const onGetAttributeScores = useCallback(async (filters, isSelf = false) => {
    if (isSelf || !isArrayEmpty(filters.reviewer)) {
      try {
        const params = {
          ...filters,
        };

        const { data } = await http.get(API_REPORT_USER_COMPARE, {
          params,
          paramsSerializer: d => parseDuplicateParameters(d),
        });

        return data;
      } catch {
        return [];
      }
    }

    return [];
  }, []);

  const handleCreatedForUserSelect = selectedUser => {
    const createdForData = compareRef?.current?.getCreatedForData();
    const compareWithData = compareRef?.current?.getCompareWithData();
    setCreatedForUser(selectedUser);
    checkValueInPresets(
      { ...createdForData, subject: selectedUser },
      compareWithData
    );
  };

  const handlePlaceholder = name => {
    setDefaultPlaceholder(false);
    setPresetPlaceholderName(name);
  };

  const handleDeletePresetDialog = preset => {
    setSelectedPreset(preset);
    setIsDeletePresetDialogOpened(true);
  };

  const handleDeletePreset = () => {
    deleteSelectedPreset(dispatch, selectedPreset.id, COMPARE_PRESET_ID).then(
      () => {
        setIsDeletePresetDialogOpened(false);
        if (presets.active[COMPARE_PRESET_ID] === selectedPreset.id) {
          setSelectedPreset(undefined);
          compareRef?.current?.updateCreatedForPreset(null);
          compareRef?.current?.updateCompareWithPreset(null);
          setInitialPreset(undefined);
          setAlreadySavedMessage('');
        }
        showSuccessMessage(translations.presets.successMessages.deletedPreset);
      }
    );
  };

  const handleSavePreset = presetName => {
    const createdForData = compareRef?.current?.getCreatedForData();
    const compareWithData = compareRef?.current?.getCompareWithData();
    const data = {
      createdFor: createdForData,
      ...(compareWithData?.subject ? { compareWith: compareWithData } : {}),
    };
    addNewPreset(dispatch, {
      name: presetName,
      location: COMPARE_PRESET_ID,
      data,
    }).then(() => {
      setAlreadySavedMessage(translations.presets.used);
      showSuccessMessage(translations.presets.successMessages.createdPreset);
    });
  };

  const handleApplyPreset = preset => {
    const presetData = preset?.data;

    setTempState(null);
    editPresetActivity(dispatch, preset.id, COMPARE_PRESET_ID, true);
    setCreatedForUser(presetData?.createdFor.subject);
    compareRef?.current?.updateCreatedForPreset(presetData?.createdFor);
    compareRef?.current?.updateCompareWithPreset(
      presetData?.compareWith || { subject: null }
    );
    setAlreadySavedMessage(translations.presets.used);
    setSelectedPreset(preset);
  };

  const handleCompareWithDisabledPresetMessage = useCallback(
    compareWithData => {
      const label = handleDisabledPresetMessage(
        compareWithData,
        translations.presets
      );
      setCompareWithDisabledPresetMessage(label || '');
    },
    [translations.presets]
  );

  const handleCreatedForDisabledPresetMessage = useCallback(
    createdForData => {
      const label = handleDisabledPresetMessage(
        createdForData,
        translations.presets
      );
      setCreatedForDisabledPresetMessage(label || '');
    },
    [translations.presets]
  );

  const handleFilterDataChange = useCallback(
    data => {
      setFilterDataForPreset(data);

      const { createdFor, compareWith } = data;
      const isCreatedForSelf = areArraysContentsEqual(createdFor?.survey_type, [
        SURVEY_TYPES.SELF,
      ]);
      const hasReviewers = !(
        !createdFor ||
        (!isCreatedForSelf && isArrayEmpty(createdFor?.reviewer))
      );
      const hasCompareWith = Boolean(compareWith?.subject);

      setCreatedForDisabledMessage(
        createdForUser && hasReviewers && hasCompareWith
          ? ''
          : translations.presets.comparePresetConditionTooltip
      );
    },
    [createdForUser, translations.presets.comparePresetConditionTooltip]
  );

  const surveyTypeOptions = prepareSurveyTypeFilterOptions(
    translations.compare.surveyTypeFilterOptions
  );

  const buildFilterSummary = (filterData, reviewerUsers, title) => {
    const isSelf = areArraysContentsEqual(filterData?.survey_type, [
      SURVEY_TYPES.SELF,
    ]);
    const surveyType = getSelectedSurveyType(
      surveyTypeOptions,
      filterData?.survey_type
    );

    const range = {
      startDate: filterData?.from_date,
      endDate: filterData?.to_date,
    };

    const reviewers =
      !isSelf && !isArrayEmpty(reviewerUsers)
        ? allUsers.filter(user => isValueInList(reviewerUsers, user.id))
        : [];

    return {
      title,
      subject: filterData?.subject,
      surveyTypes: surveyType?.label || '',
      range,
      showReviewers: !isSelf,
      reviewers,
    };
  };

  const presetSummary = {
    hasCompareFilters: true,
    translations: {
      surveyType: translations.compare.surveyType.label,
      range: translations.compare.period,
      reviewers: translations.compare.reviewersPicker.addPeopleLabel,
    },
    compareFilters: [
      buildFilterSummary(
        filterDataForPreset.createdFor,
        filterDataForPreset.createdForReviewers,
        translations.user.label
      ),
      ...(filterDataForPreset.compareWith?.subject
        ? [
            buildFilterSummary(
              filterDataForPreset.compareWith,
              filterDataForPreset.compareWithReviewers,
              translations.compare.addCompareWithDialog.label
            ),
          ]
        : []),
    ],
  };

  return (
    <PageContainer
      {...rest}
      translations={translations}
      auth={auth}
      accessControl={accessControl}
      navigate={navigate}
      shouldPassProps={false}
    >
      {!isLoading && (
        <div className={classes.root}>
          <Typography className={classes.pageDescription} variant="body2">
            {translations.description}
          </Typography>
          <div className={classes.userSelectWrapper}>
            <SelectField
              className={classes.userSelect}
              label={translations.user.label}
              placeholder={translations.user.placeholder}
              parser={{
                value: 'id',
                label: 'first_name',
              }}
              value={createdForUser}
              options={allAccessibleUsers}
              onChange={handleCreatedForUserSelect}
              shouldReturnOption
              isUser
            />
            <PresetDropdown
              translations={translations.presets}
              dropdownWidth={DROPDOWN_WIDTH}
              options={filteredPresets}
              activePreset={activePresetId}
              handleApply={handleApplyPreset}
              handleDelete={handleDeletePresetDialog}
              handleSavePreset={handleSavePreset}
              disabledPreset={
                handleHasMaxPresets(filteredPresets, translations.presets) ||
                compareWithDisabledPresetMessage ||
                createdForDisabledPresetMessage ||
                createdForDisabledMessage ||
                (!createdForUser
                  ? translations.presets.comparePresetConditionTooltip
                  : '')
              }
              alreadySavedMessage={alreadySavedMessage}
              presetPlaceholderName={placeholderName}
              presetSummary={presetSummary}
              handlePlaceholder={handlePlaceholder}
            />
          </div>
          {createdForUser ? (
            <UserCompare
              ref={compareRef}
              translations={translations.compare}
              initialReviewers={initialReviewers}
              initialSurveyType={initialSurveyType}
              initialPeriod={initialPeriod}
              surveyTypeOptions={surveyTypeOptions}
              allUsers={allUsers}
              allAccessibleUsers={allAccessibleUsers}
              userFilters={userFilters}
              createdForUser={createdForUser}
              onGetAttributeScores={onGetAttributeScores}
              onCompareWithDisabledPresetMessage={
                handleCompareWithDisabledPresetMessage
              }
              onCreatedForDisabledPresetMessage={
                handleCreatedForDisabledPresetMessage
              }
              onFilterDataChange={handleFilterDataChange}
              canAccessUserProfileCheck={canAccessUserProfileCheck}
              onGoToProfilePage={goToUserProfilePage}
              onGetReviewers={onGetReviewers}
              initialPreset={initialPreset}
              checkValueInPresets={checkValueInPresets}
            />
          ) : (
            <NotificationCard content={translations.noResults} />
          )}
        </div>
      )}
      <AlertDialog
        translations={translations.presets.presetDeleteDialog}
        isOpened={isDeletePresetDialogOpened}
        onClose={() => setIsDeletePresetDialogOpened(false)}
        onConfirm={handleDeletePreset}
        isWarning
      />
    </PageContainer>
  );
};

CompareReportPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  presets: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default memo(CompareReportPage);
